/**
 * Admin Dashboard Overview Page
 * Displays key metrics and statistics for platform administration
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CreditCard,
  Cpu,
  DollarSign,
  Inbox,
  Mail,
  PieChart,
  Receipt,
  Send,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react';
import {
  useAdminDashboardOverview,
  useEmailIntegrationMonitoring,
} from '../../services/dashboard.service';
import { formatAdminNumber } from '../../utils/numberFormat';
import { FeatureUsageSection } from '../../features/admin/feature-tracking';
import type {
  DashboardChartGranularity,
  DashboardChartPoint,
  DashboardEmailIntegrationMonitoring,
  DashboardRegistrationChannelBreakdown,
  DashboardRegistrationProvider,
  DashboardTokenUsageChartPoint,
  RecentActivity,
} from '../../types/admin';

// ============================================
// Types
// ============================================

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  detail?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: string;
  isLoading?: boolean;
}

// ============================================
// Stat Card Component
// ============================================

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  detail,
  trend,
  color,
  isLoading,
}) => {
  return (
    <Paper className="flex h-full min-h-[176px] w-full rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800">
      <Box className="flex w-full items-start justify-between">
        <Box className="flex min-h-full flex-1 flex-col">
          <Typography
            variant="body2"
            className="mb-1 font-medium text-gray-600 dark:text-slate-400"
          >
            {title}
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={120} height={44} />
          ) : (
            <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
              {value}
            </Typography>
          )}
          {!isLoading && detail}
          {trend && (
            <Box className="mt-auto flex flex-wrap items-center gap-1 pt-2">
              {trend.isPositive ? (
                <TrendingUp size={16} className="text-green-500" />
              ) : (
                <TrendingDown size={16} className="text-red-500" />
              )}
              <Typography
                variant="caption"
                className={trend.isPositive ? 'text-green-600' : 'text-red-600'}
              >
                {formatPercentage(trend.value)}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-500">
                vs previous period
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={`flex h-12 w-12 items-center justify-center rounded-lg ${color}`}>
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

interface DashboardAnalyticsChartProps {
  data?: DashboardChartPoint[];
  tokenData?: DashboardTokenUsageChartPoint[];
  currency?: string;
  granularity: DashboardChartGranularity;
  isLoading?: boolean;
  onGranularityChange: (granularity: DashboardChartGranularity) => void;
}

interface DashboardAnalyticsChartPoint extends DashboardChartPoint {
  totalTokens: number;
}

interface RegistrationChannelsChartProps {
  data?: DashboardRegistrationChannelBreakdown;
  isLoading?: boolean;
  includeAnonymous: boolean;
  onIncludeAnonymousChange: (includeAnonymous: boolean) => void;
}

interface EmailIntegrationMonitoringPanelProps {
  data?: DashboardEmailIntegrationMonitoring;
  isLoading?: boolean;
  isError?: boolean;
}

const chartGranularityLabels: Record<DashboardChartGranularity, string> = {
  day: 'Day',
  month: 'Month',
  year: 'Year',
};

const DashboardAnalyticsChart: React.FC<DashboardAnalyticsChartProps> = ({
  data = [],
  tokenData = [],
  currency = 'VND',
  granularity,
  isLoading,
  onGranularityChange,
}) => {
  const tokenDataByKey = new Map(tokenData.map((item) => [item.key, item]));
  const chartData: DashboardAnalyticsChartPoint[] = [
    ...data.map((item) => ({
      ...item,
      totalTokens: tokenDataByKey.get(item.key)?.totalTokens ?? 0,
    })),
    ...tokenData
      .filter((item) => !data.some((chartItem) => chartItem.key === item.key))
      .map((item) => ({
        key: item.key,
        label: item.label,
        users: 0,
        revenue: 0,
        totalTokens: item.totalTokens,
      })),
  ];
  const maxUsers = Math.max(...chartData.map((item) => item.users), 1);
  const maxRevenue = Math.max(...chartData.map((item) => item.revenue), 1);
  const maxTokens = Math.max(...chartData.map((item) => item.totalTokens), 1);
  const totalUsers = chartData.reduce((sum, item) => sum + item.users, 0);
  const totalRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);
  const totalTokens = chartData.reduce((sum, item) => sum + item.totalTokens, 0);
  const groupWidth = 72;
  const chartHeight = 300;
  const plotHeight = 220;
  const labelTop = 248;
  const svgWidth = Math.max(chartData.length * groupWidth, 560);
  const tokenLinePoints = chartData
    .map((item, index) => {
      const x = index * groupWidth + groupWidth / 2;
      const y = plotHeight - (item.totalTokens / maxTokens) * (plotHeight - 12) + 8;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <Paper className="h-full w-full rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800">
      <Box className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <Box>
          <Box className="mb-2 flex items-center gap-2">
            <BarChart3 size={22} className="text-sky-600 dark:text-sky-400" />
            <Typography variant="h6" className="font-bold text-gray-900 dark:text-slate-100">
              Users / Revenue / Token Usage
            </Typography>
          </Box>
          <Box className="flex flex-wrap items-center gap-3">
            <Box className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-sky-500" />
              <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
                Users: {formatCompactNumber(totalUsers)}
              </Typography>
            </Box>
            <Box className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-emerald-500" />
              <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
                Revenue: {formatCurrency(totalRevenue, currency)}
              </Typography>
            </Box>
            <Box className="flex items-center gap-2">
              <span className="h-0.5 w-4 rounded-full bg-violet-500" />
              <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
                Tokens: {formatCompactNumber(totalTokens)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <ButtonGroup size="small" variant="outlined" aria-label="Chart period">
          {(Object.keys(chartGranularityLabels) as DashboardChartGranularity[]).map((key) => (
            <Button
              key={key}
              variant={granularity === key ? 'contained' : 'outlined'}
              onClick={() => onGranularityChange(key)}
            >
              {chartGranularityLabels[key]}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={300} />
      ) : chartData.length ? (
        <Box className="overflow-x-auto pb-1">
          <svg
            viewBox={`0 0 ${svgWidth} ${chartHeight}`}
            style={{ width: '100%', minWidth: `${svgWidth}px`, height: `${chartHeight}px` }}
            role="img"
            aria-label="Users, revenue, and token usage chart"
          >
            <line
              x1="0"
              y1={plotHeight + 1}
              x2={svgWidth}
              y2={plotHeight + 1}
              stroke="rgb(229 231 235)"
              strokeWidth="1"
            />
            {chartData.map((item, index) => {
              const x = index * groupWidth + groupWidth / 2;
              const usersHeight = item.users > 0 ? Math.max((item.users / maxUsers) * 208, 8) : 0;
              const revenueHeight =
                item.revenue > 0 ? Math.max((item.revenue / maxRevenue) * 208, 8) : 0;

              return (
                <g
                  key={item.key}
                  className="transition-opacity hover:opacity-80"
                  aria-label={`${item.label}: ${item.users} users, ${formatCurrency(
                    item.revenue,
                    currency
                  )}, ${formatAdminNumber(item.totalTokens)} tokens`}
                >
                  <title>
                    {`${item.label}: ${item.users} users, ${formatCurrency(
                      item.revenue,
                      currency
                    )}, ${formatAdminNumber(item.totalTokens)} tokens`}
                  </title>
                  <rect
                    x={x - 15}
                    y={plotHeight - usersHeight}
                    width="12"
                    height={usersHeight}
                    rx="3"
                    fill="#0ea5e9"
                  />
                  {item.users > 0 && (
                    <text
                      x={x - 9}
                      y={Math.max(12, plotHeight - usersHeight - 7)}
                      textAnchor="middle"
                      className="fill-sky-700 text-[10px] font-semibold dark:fill-sky-300"
                    >
                      {formatCompactNumber(item.users)}
                    </text>
                  )}
                  <rect
                    x={x + 3}
                    y={plotHeight - revenueHeight}
                    width="12"
                    height={revenueHeight}
                    rx="3"
                    fill="#10b981"
                  />
                  {item.revenue > 0 && (
                    <text
                      x={x + 9}
                      y={Math.max(24, plotHeight - revenueHeight - 7)}
                      textAnchor="middle"
                      className="fill-emerald-700 text-[10px] font-semibold dark:fill-emerald-300"
                    >
                      {formatCompactCurrency(item.revenue, currency)}
                    </text>
                  )}
                  <text
                    x={x}
                    y={labelTop}
                    textAnchor="middle"
                    className="fill-gray-500 text-[11px] dark:fill-slate-400"
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}
            <polyline
              points={tokenLinePoints}
              fill="none"
              stroke="#8b5cf6"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {chartData.map((item, index) => {
              const x = index * groupWidth + groupWidth / 2;
              const y = plotHeight - (item.totalTokens / maxTokens) * (plotHeight - 12) + 8;

              return (
                <g key={`${item.key}-tokens`}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    fill="#8b5cf6"
                    stroke="#ffffff"
                    strokeWidth="2"
                  >
                    <title>{`${item.label}: ${formatAdminNumber(item.totalTokens)} tokens`}</title>
                  </circle>
                  {item.totalTokens > 0 && (
                    <text
                      x={x}
                      y={Math.max(12, y - 10)}
                      textAnchor="middle"
                      className="fill-violet-700 text-[10px] font-semibold dark:fill-violet-300"
                    >
                      {formatCompactNumber(item.totalTokens)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </Box>
      ) : (
        <Box className="flex h-72 items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-slate-700">
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
            No chart data
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const registrationProviderColors: Record<DashboardRegistrationProvider, string> = {
  EMAIL: '#2563eb',
  GOOGLE: '#dc2626',
  FACEBOOK: '#4f46e5',
  APPLE: '#475569',
  GITHUB: '#171717',
  ANONYMOUS: '#d97706',
  ADMIN_CREATED: '#059669',
  UNKNOWN: '#9ca3af',
};

const RegistrationChannelsChart: React.FC<RegistrationChannelsChartProps> = ({
  data,
  isLoading,
  includeAnonymous,
  onIncludeAnonymousChange,
}) => {
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  let cursor = 0;
  const gradientSegments = items.map((item) => {
    const start = cursor;
    const end = cursor + item.percentage;
    cursor = end;
    return `${registrationProviderColors[item.provider]} ${start}% ${end}%`;
  });

  return (
    <Paper className="h-full w-full rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800">
      <Box className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <Box>
          <Box className="mb-2 flex items-center gap-2">
            <PieChart size={22} className="text-emerald-600 dark:text-emerald-400" />
            <Typography variant="h6" className="font-bold text-gray-900 dark:text-slate-100">
              Registration Channels
            </Typography>
          </Box>
          <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
            {includeAnonymous ? 'Total users' : 'Registered users'}: {formatCompactNumber(total)}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={includeAnonymous}
              onChange={(event) => onIncludeAnonymousChange(event.target.checked)}
              size="small"
            />
          }
          label="Include anonymous"
          sx={{
            m: 0,
            color: (theme) =>
              theme.palette.mode === 'dark' ? 'rgb(203, 213, 225)' : 'rgb(75, 85, 99)',
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={300} />
      ) : items.length ? (
        <Box className="flex min-h-[300px] flex-col items-center justify-center gap-6">
          <Box
            className="relative h-52 w-52 shrink-0 rounded-full"
            style={{
              background: `conic-gradient(${gradientSegments.join(', ')})`,
            }}
            title={`${formatCompactNumber(total)} registered users`}
          >
            <Box className="absolute inset-10 flex flex-col items-center justify-center rounded-full bg-white dark:bg-slate-800">
              <Typography variant="h5" className="font-bold text-gray-900 dark:text-slate-100">
                {formatCompactNumber(total)}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                users
              </Typography>
            </Box>
          </Box>

          <Box className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
            {items.map((item) => (
              <Box key={item.provider} className="flex min-w-0 items-center gap-3">
                <span
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: registrationProviderColors[item.provider] }}
                />
                <Typography
                  variant="body2"
                  className="min-w-0 flex-1 truncate text-gray-700 dark:text-slate-300"
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  className="shrink-0 font-semibold text-gray-900 dark:text-slate-100"
                >
                  {formatCompactNumber(item.count)}
                </Typography>
                <Typography
                  variant="caption"
                  className="w-12 shrink-0 text-right text-gray-500 dark:text-slate-400"
                >
                  {item.percentage}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box className="flex h-72 items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-slate-700">
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
            No registration channel data
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const EmailIntegrationMonitoringPanel: React.FC<EmailIntegrationMonitoringPanelProps> = ({
  data,
  isLoading,
  isError,
}) => {
  const statusItems = data?.accountsByProviderStatus ?? [];
  const recentFailures = data?.recentFailures ?? [];

  return (
    <Paper className="h-full w-full rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800">
      <Box className="mb-5 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <Box>
          <Box className="mb-2 flex items-center gap-2">
            <Mail size={22} className="text-cyan-600 dark:text-cyan-400" />
            <Typography variant="h6" className="font-bold text-gray-900 dark:text-slate-100">
              Email Integration
            </Typography>
          </Box>
          <Typography variant="caption" className="text-gray-600 dark:text-slate-400">
            Last {data?.window.hours ?? 24}h operational health
          </Typography>
        </Box>
        {data && (
          <Chip
            size="small"
            label={`${formatCompactNumber(data.totals.failedEventsInWindow)} failures`}
            className={
              data.totals.failedEventsInWindow > 0
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
            }
          />
        )}
      </Box>

      {isLoading ? (
        <Box className="space-y-4">
          <Skeleton variant="rounded" height={92} />
          <Skeleton variant="rounded" height={132} />
        </Box>
      ) : isError ? (
        <Alert severity="warning">Unable to load email integration monitoring</Alert>
      ) : data ? (
        <Box className="space-y-5">
          <Box className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <EmailMetric
              icon={<Mail size={18} />}
              label="Accounts"
              value={data.totals.accounts}
              tone="cyan"
            />
            <EmailMetric
              icon={<Inbox size={18} />}
              label="Messages"
              value={data.totals.messages}
              tone="blue"
            />
            <EmailMetric
              icon={<AlertTriangle size={18} />}
              label="Failed"
              value={data.totals.failedMessages}
              tone="red"
            />
            <EmailMetric
              icon={<CheckCircle2 size={18} />}
              label="Approvals"
              value={data.totals.pendingApprovals}
              tone="emerald"
            />
            <EmailMetric
              icon={<Send size={18} />}
              label="Send fails"
              value={data.totals.failedSendsInWindow}
              tone="amber"
            />
          </Box>

          <Box className="grid gap-5 lg:grid-cols-2">
            <Box>
              <Typography variant="subtitle2" className="mb-3 font-semibold text-gray-900 dark:text-slate-100">
                Provider Status
              </Typography>
              <Box className="space-y-2">
                {statusItems.length ? (
                  statusItems.map((item) => (
                    <Box
                      key={`${item.provider}-${item.status}`}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2 dark:border-slate-700"
                    >
                      <Typography variant="body2" className="min-w-0 truncate text-gray-700 dark:text-slate-300">
                        {item.provider}
                      </Typography>
                      <Box className="flex shrink-0 items-center gap-2">
                        <Chip
                          size="small"
                          label={item.status}
                          className={`capitalize ${getEmailStatusBadgeClass(item.status)}`}
                        />
                        <Typography variant="body2" className="w-10 text-right font-semibold text-gray-900 dark:text-slate-100">
                          {formatCompactNumber(item.count)}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                    No connected email accounts
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" className="mb-3 font-semibold text-gray-900 dark:text-slate-100">
                Recent Failures
              </Typography>
              <Box className="space-y-2">
                {recentFailures.length ? (
                  recentFailures.slice(0, 5).map((failure) => (
                    <Box
                      key={failure.publicId}
                      className="min-w-0 rounded-lg border border-red-100 px-3 py-2 dark:border-red-900/40"
                    >
                      <Box className="mb-1 flex min-w-0 items-center justify-between gap-2">
                        <Typography variant="body2" className="min-w-0 truncate font-medium text-gray-900 dark:text-slate-100">
                          {failure.eventType}
                        </Typography>
                        <Typography variant="caption" className="shrink-0 text-gray-500 dark:text-slate-400">
                          {formatRelativeTime(failure.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" className="block truncate text-gray-600 dark:text-slate-400">
                        {failure.emailAddress || failure.messagePublicId || 'Unresolved provider event'}
                      </Typography>
                      {failure.message && (
                        <Typography variant="caption" className="block truncate text-red-600 dark:text-red-300">
                          {failure.message}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                    No failures in this window
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {data.totals.gmailWatchNeedsAttention > 0 && (
            <Alert severity="warning">
              {formatCompactNumber(data.totals.gmailWatchNeedsAttention)} Gmail watches need setup or renewal within 24h.
            </Alert>
          )}
        </Box>
      ) : (
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          No email integration monitoring data
        </Typography>
      )}
    </Paper>
  );
};

interface EmailMetricProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: 'amber' | 'blue' | 'cyan' | 'emerald' | 'red';
}

const emailMetricToneClasses: Record<EmailMetricProps['tone'], string> = {
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  cyan: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300',
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
};

const EmailMetric: React.FC<EmailMetricProps> = ({ icon, label, value, tone }) => (
  <Box className={`rounded-lg px-3 py-3 ${emailMetricToneClasses[tone]}`}>
    <Box className="mb-2 flex items-center gap-2">
      {icon}
      <Typography variant="caption" className="font-medium">
        {label}
      </Typography>
    </Box>
    <Typography variant="h6" className="font-bold">
      {formatCompactNumber(value)}
    </Typography>
  </Box>
);

const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    notation: value >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
};

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'VND' ? 0 : 2,
  }).format(amount);
};

const formatCompactCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: currency === 'VND' ? 0 : 1,
  }).format(amount);
};

const formatPercentage = (value: number): string => {
  if (value === 0) return '0%';
  return `${value > 0 ? '+' : ''}${value}%`;
};

const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

const getActivityIcon = (activity: RecentActivity) => {
  const iconClass =
    activity.severity === 'error'
      ? 'text-red-600 dark:text-red-400'
      : activity.severity === 'warning'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-blue-600 dark:text-blue-400';

  switch (activity.type) {
    case 'user_registration':
      return <UserPlus size={18} className={iconClass} />;
    case 'subscription_change':
      return <CreditCard size={18} className={iconClass} />;
    case 'payment_success':
      return <Receipt size={18} className={iconClass} />;
    case 'api_error':
      return <AlertTriangle size={18} className={iconClass} />;
    case 'high_usage':
      return <Zap size={18} className={iconClass} />;
    default:
      return <Users size={18} className={iconClass} />;
  }
};

const getActivityBadgeClass = (severity?: RecentActivity['severity']): string => {
  if (severity === 'error') {
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  }

  if (severity === 'warning') {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }

  return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
};

const getEmailStatusBadgeClass = (status: string): string => {
  if (status === 'active' || status === 'ingested' || status === 'sent') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  }

  if (status === 'error' || status === 'failed') {
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  }

  if (status === 'disabled' || status === 'revoked') {
    return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300';
  }

  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
};

// ============================================
// Admin Dashboard Component
// ============================================

export const AdminDashboard: React.FC = () => {
  const [includeAnonymousChannels, setIncludeAnonymousChannels] = useState(false);
  const { data, isLoading, isError, error } = useAdminDashboardOverview({
    includeAnonymous: includeAnonymousChannels,
  });
  const {
    data: emailMonitoring,
    isLoading: isEmailMonitoringLoading,
    isError: isEmailMonitoringError,
  } = useEmailIntegrationMonitoring({ windowHours: 24, limit: 20 });
  const [chartGranularity, setChartGranularity] = useState<DashboardChartGranularity>('day');
  const chartData = useMemo(
    () => data?.charts?.userRevenue[chartGranularity] ?? [],
    [chartGranularity, data?.charts?.userRevenue]
  );
  const tokenChartData = useMemo(
    () => data?.charts?.tokenUsage?.[chartGranularity] ?? [],
    [chartGranularity, data?.charts?.tokenUsage]
  );
  const verifiedPercentage =
    data && data.stats.totalUsers > 0
      ? Math.round((data.stats.verifiedUsers / data.stats.totalUsers) * 1000) / 10
      : 0;

  const stats = [
    {
      title: 'Total Users',
      value: formatCompactNumber(data?.stats.totalUsers ?? 0),
      detail: data ? (
        <Box className="mb-2 flex flex-wrap items-center gap-2">
          <Chip
            size="small"
            icon={<CheckCircle2 size={14} />}
            label={`Verified OTP: ${formatCompactNumber(data.stats.verifiedUsers)}`}
            className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
          />
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            {verifiedPercentage}% of total
          </Typography>
        </Box>
      ) : undefined,
      icon: <Users size={24} className="text-blue-600 dark:text-blue-400" />,
      trend: data
        ? { value: data.stats.userGrowth, isPositive: data.stats.userGrowth >= 0 }
        : undefined,
      color: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      title: 'Active Subscriptions',
      value: formatCompactNumber(data?.stats.activeSubscriptions ?? 0),
      icon: <CreditCard size={24} className="text-emerald-600 dark:text-emerald-400" />,
      trend: data
        ? {
            value: data.stats.subscriptionGrowth,
            isPositive: data.stats.subscriptionGrowth >= 0,
          }
        : undefined,
      color: 'bg-emerald-100 dark:bg-emerald-900/30',
    },
    {
      title: 'Total AI Tokens Used',
      value: formatCompactNumber(data?.stats.totalTokensUsed ?? 0),
      icon: <Cpu size={24} className="text-purple-600 dark:text-purple-400" />,
      trend: data
        ? {
            value: data.stats.tokenUsageGrowth,
            isPositive: data.stats.tokenUsageGrowth >= 0,
          }
        : undefined,
      color: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      title: 'Revenue (MTD)',
      value: data
        ? formatCurrency(data.stats.revenueMtd.amount, data.stats.revenueMtd.currency)
        : '0',
      icon: <DollarSign size={24} className="text-amber-600 dark:text-amber-400" />,
      trend: data
        ? {
            value: data.stats.revenueGrowth,
            isPositive: data.stats.revenueGrowth >= 0,
          }
        : undefined,
      color: 'bg-amber-100 dark:bg-amber-900/30',
    },
  ];

  return (
    <Box className="min-h-screen p-8">
      {/* Page Header */}
      <Box className="mb-8">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          Dashboard Overview
        </Typography>
        <Typography variant="body1" className="text-gray-600 dark:text-slate-400">
          Monitor key metrics and platform performance
        </Typography>
      </Box>

      {isError && (
        <Alert severity="error" className="mb-6">
          {error?.message || 'Unable to load dashboard overview'}
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} className="mb-8">
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index} className="flex">
            <StatCard {...stat} isLoading={isLoading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} className="mb-8">
        <Grid item xs={12} className="flex">
          <FeatureUsageSection />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} className="mb-8">
        <Grid item xs={12} md={6} className="flex">
          <DashboardAnalyticsChart
            data={chartData}
            tokenData={tokenChartData}
            currency={data?.stats.revenueMtd.currency}
            granularity={chartGranularity}
            isLoading={isLoading}
            onGranularityChange={setChartGranularity}
          />
        </Grid>
        <Grid item xs={12} md={6} className="flex">
          <RegistrationChannelsChart
            data={data?.charts?.registrationChannels}
            isLoading={isLoading}
            includeAnonymous={includeAnonymousChannels}
            onIncludeAnonymousChange={setIncludeAnonymousChannels}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} className="mb-8">
        <Grid item xs={12}>
          <EmailIntegrationMonitoringPanel
            data={emailMonitoring}
            isLoading={isEmailMonitoringLoading}
            isError={isEmailMonitoringError}
          />
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className="rounded-lg bg-white p-6 shadow-sm dark:bg-slate-800">
            <Typography variant="h6" className="mb-4 font-bold text-gray-900 dark:text-slate-100">
              Recent Activity
            </Typography>
            {isLoading ? (
              <Box className="space-y-3">
                {[0, 1, 2, 3].map((item) => (
                  <Box
                    key={item}
                    className="flex items-center gap-3 rounded-lg border border-gray-100 p-4 dark:border-slate-700"
                  >
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box className="flex-1">
                      <Skeleton variant="text" width="45%" />
                      <Skeleton variant="text" width="20%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : data?.recentActivities.length ? (
              <Box className="divide-y divide-gray-100 dark:divide-slate-700">
                {data.recentActivities.map((activity) => (
                  <Box
                    key={activity.id}
                    className="flex items-center gap-4 py-4 first:pt-0 last:pb-0"
                  >
                    <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-700">
                      {getActivityIcon(activity)}
                    </Box>
                    <Box className="min-w-0 flex-1">
                      <Typography
                        variant="body2"
                        className="truncate font-medium text-gray-900 dark:text-slate-100"
                      >
                        {activity.message}
                      </Typography>
                      <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                        {formatRelativeTime(activity.timestamp)}
                      </Typography>
                    </Box>
                    <Chip
                      label={activity.severity || 'info'}
                      size="small"
                      className={`capitalize ${getActivityBadgeClass(activity.severity)}`}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className="flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-slate-700">
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                  No recent activity
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
